import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft } from 'lucide-react';
import {
  // Icônes actuellement utilisées
  Hash, Target, Ship, MessageSquare, Calendar, Play, HelpCircle, Trophy,
  Telescope, MapPin, Activity, Compass, Users, Lightbulb, Waves, FolderOpen,
  Bell, Settings, BarChart3, Search, LogOut, Anchor, Shield, Award, Star,
  Send, ThumbsUp, Pencil, Trash2, CheckCircle, XCircle, Heart, Eye,
} from 'lucide-react';

// Palette dossier
const PALETTE = {
  gold:    'hsl(33 45% 38%)',  // gold-dark — actions premium
  goldLight: 'hsl(34 44% 50%)', // gold standard
  ink:     'hsl(218 48% 16%)', // encre — neutre / important
  teal:    'hsl(177 60% 21%)', // teal — positif / progression
  red:     'hsl(8 54% 39%)',   // bordeaux — danger / alerte
  muted:   'hsl(223 11% 53%)', // muted — secondaire
  paper2:  'hsl(38 35% 92%)',  // papier-2 fond
};

// Classification : quelle couleur pour quel concept ?
//  - Gold = actions phares / navigation principale / premium
//  - Ink = neutre / informationnel / contenu écrit
//  - Teal = progression / positif / croissance
//  - Red = alerte / vigie / suppression
//  - Muted = secondaire

const NAV_TILES = [
  { icon: Hash,         label: 'Channels',     desc: 'Discussion équipage', color: 'gold' },
  { icon: Target,       label: 'Défis',        desc: 'Objectifs quotidiens', color: 'teal' },
  { icon: Ship,         label: 'Mon Odyssée',  desc: 'Parcours formation', color: 'ink' },
  { icon: MessageSquare,label: 'Messages',     desc: 'Privés', color: 'gold' },
  { icon: Calendar,     label: 'Calendrier',   desc: 'Événements', color: 'ink' },
  { icon: Play,         label: 'Replays',      desc: 'Vidéos', color: 'muted' },
  { icon: HelpCircle,   label: 'Q&A',          desc: 'Questions/Réponses', color: 'ink' },
  { icon: Trophy,       label: 'Victoires',    desc: 'Succès partagés', color: 'gold' },
  { icon: Telescope,    label: 'Vigie Immo',   desc: 'Veille marché', color: 'red' },
  { icon: MapPin,       label: 'Carte',        desc: 'Membres géoloc', color: 'ink' },
  { icon: Activity,     label: 'Actualité',    desc: 'Feed communauté', color: 'teal' },
  { icon: Compass,      label: 'Objectifs',    desc: 'Personnels', color: 'gold' },
  { icon: Users,        label: 'Ambassadeur',  desc: 'Parrainage', color: 'teal' },
  { icon: Lightbulb,    label: 'Idées',        desc: 'Suggestions', color: 'gold' },
  { icon: Waves,        label: 'Saisons',      desc: 'Cycles', color: 'ink' },
  { icon: FolderOpen,   label: 'Ressources',   desc: 'Fichiers', color: 'muted' },
];

const HEADER_ICONS = [
  { icon: Search,    label: 'Rechercher',     color: 'muted' },
  { icon: Bell,      label: 'Notifications',  color: 'gold' },
  { icon: MessageSquare, label: 'Channels',   color: 'gold' },
  { icon: BarChart3, label: 'Analytics',      color: 'ink' },
  { icon: Settings,  label: 'Admin',          color: 'ink' },
  { icon: LogOut,    label: 'Déconnexion',    color: 'muted' },
];

const ACTION_ICONS = [
  { icon: Send,        label: 'Envoyer',     color: 'gold' },
  { icon: ThumbsUp,    label: 'Like',        color: 'gold' },
  { icon: Heart,       label: 'Favori',      color: 'red' },
  { icon: Eye,         label: 'Voir',        color: 'ink' },
  { icon: Pencil,      label: 'Modifier',    color: 'muted' },
  { icon: Trash2,      label: 'Supprimer',   color: 'red' },
  { icon: CheckCircle, label: 'Valider',     color: 'teal' },
  { icon: XCircle,     label: 'Annuler',     color: 'muted' },
];

const NavTile = ({ Icon, label, desc, color }) => (
  <button className="dossier-card group h-auto py-4 px-3 flex flex-col items-center gap-2 transition-all" style={{ padding: '1rem 0.75rem' }}>
    <div
      className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
      style={{
        background: color === 'gold' ? 'hsl(41 67% 95%)'
                  : color === 'teal' ? 'hsl(167 27% 89% / 0.6)'
                  : color === 'red' ? 'hsl(8 54% 39% / 0.07)'
                  : color === 'muted' ? PALETTE.paper2
                  : PALETTE.paper2,
        border: `1px solid ${color === 'gold' ? 'hsl(38 51% 84%)'
                  : color === 'teal' ? 'hsl(177 60% 21% / 0.2)'
                  : color === 'red' ? 'hsl(8 54% 39% / 0.2)'
                  : 'hsl(41 30% 85% / 0.5)'}`,
      }}
    >
      <Icon className="w-5 h-5" style={{ color: PALETTE[color] }} />
    </div>
    <span className="text-xs font-semibold text-ink">{label}</span>
    <span className="text-[10px] italic text-muted-dossier text-center">{desc}</span>
  </button>
);

const HeaderBtn = ({ Icon, label, color }) => (
  <div className="flex flex-col items-center gap-2">
    <button className="w-10 h-10 rounded-full hover:bg-paper-2 flex items-center justify-center transition-colors">
      <Icon className="w-5 h-5" style={{ color: PALETTE[color] }} />
    </button>
    <span className="text-[10px] italic text-muted-dossier">{label}</span>
  </div>
);

const IconsColorPreviewPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user || user.role !== 'admin') {
    return <div className="min-h-screen flex items-center justify-center bg-paper"><p>Accès réservé aux admins.</p></div>;
  }

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="icons-color-preview">
      <header className="border-b border-line bg-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-4">
          <button onClick={() => navigate('/icons-preview')} className="btn-pill-secondary">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div>
            <h1 className="text-2xl m-0 filet-gold">Mêmes icônes — palette Dossier</h1>
            <p className="italic text-sm text-muted-dossier mt-3 mb-0">
              Lucide icons inchangées, recolorées avec encre/or/teal/bordeaux/muted uniquement.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">

        {/* Légende palette */}
        <section className="dossier-card mb-8" style={{ padding: '1.5rem' }}>
          <p className="dossier-label" style={{ margin: 0 }}>§ Légende — code couleur sémantique</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 rounded-full" style={{ background: PALETTE.gold }} />
              <div>
                <p className="text-sm font-semibold m-0 text-ink">Or</p>
                <p className="text-[10px] italic m-0 text-muted-dossier">Actions phares</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 rounded-full" style={{ background: PALETTE.ink }} />
              <div>
                <p className="text-sm font-semibold m-0 text-ink">Encre</p>
                <p className="text-[10px] italic m-0 text-muted-dossier">Neutre / Info</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 rounded-full" style={{ background: PALETTE.teal }} />
              <div>
                <p className="text-sm font-semibold m-0 text-ink">Teal</p>
                <p className="text-[10px] italic m-0 text-muted-dossier">Progression</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 rounded-full" style={{ background: PALETTE.red }} />
              <div>
                <p className="text-sm font-semibold m-0 text-ink">Bordeaux</p>
                <p className="text-[10px] italic m-0 text-muted-dossier">Alerte / Vigie</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 rounded-full" style={{ background: PALETTE.muted }} />
              <div>
                <p className="text-sm font-semibold m-0 text-ink">Muted</p>
                <p className="text-[10px] italic m-0 text-muted-dossier">Secondaire</p>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation tiles */}
        <section className="dossier-card mb-8" style={{ padding: '1.5rem 1.75rem' }}>
          <p className="dossier-label" style={{ margin: 0 }}>§ 01 — Navigation principale</p>
          <h2 className="text-2xl mt-1 mb-3 filet-gold">Carreaux Quick Nav</h2>
          <p className="italic text-sm text-muted-dossier mb-4">
            Aujourd'hui : 16 couleurs Tailwind différentes (blue/yellow/cyan/violet/red/pink/emerald/purple/green/teal/orange/amber/indigo).
            Avec la palette dossier : 5 couleurs sémantiques cohérentes.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {NAV_TILES.map(t => (
              <NavTile key={t.label} Icon={t.icon} label={t.label} desc={t.desc} color={t.color} />
            ))}
          </div>
        </section>

        {/* Header buttons */}
        <section className="dossier-card mb-8" style={{ padding: '1.5rem 1.75rem' }}>
          <p className="dossier-label" style={{ margin: 0 }}>§ 02 — Barre supérieure</p>
          <h2 className="text-2xl mt-1 mb-3 filet-gold">Header / TopBar</h2>
          <div className="flex flex-wrap items-start gap-6 p-5 bg-white border border-line rounded-lg">
            {HEADER_ICONS.map(h => (
              <HeaderBtn key={h.label} Icon={h.icon} label={h.label} color={h.color} />
            ))}
          </div>
        </section>

        {/* Action buttons */}
        <section className="dossier-card mb-8" style={{ padding: '1.5rem 1.75rem' }}>
          <p className="dossier-label" style={{ margin: 0 }}>§ 03 — Actions in-line</p>
          <h2 className="text-2xl mt-1 mb-3 filet-gold">Boutons d'action</h2>
          <div className="flex flex-wrap items-start gap-6 p-5 bg-white border border-line rounded-lg">
            {ACTION_ICONS.map(a => (
              <HeaderBtn key={a.label} Icon={a.icon} label={a.label} color={a.color} />
            ))}
          </div>
        </section>

        {/* Comparaison: rôles AVANT/APRÈS */}
        <section className="dossier-card mb-8" style={{ padding: '1.5rem 1.75rem' }}>
          <p className="dossier-label" style={{ margin: 0 }}>§ 04 — Badges de rôles</p>
          <h2 className="text-2xl mt-1 mb-3 filet-gold">Rôles utilisateurs</h2>

          <p className="dossier-label mt-4 mb-2">Avant (couleurs Tailwind)</p>
          <div className="flex flex-wrap gap-2 p-4 bg-paper-2/40 rounded-lg border border-line">
            <span className="px-3 py-1.5 rounded-full bg-red-500/10 text-red-500 border border-red-500/30 text-xs font-semibold">Admin</span>
            <span className="px-3 py-1.5 rounded-full bg-yellow-500/10 text-yellow-500 border border-yellow-500/30 text-xs font-semibold">Major</span>
            <span className="px-3 py-1.5 rounded-full bg-green-500/10 text-green-500 border border-green-500/30 text-xs font-semibold">Ambassadeur</span>
            <span className="px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-500 border border-blue-500/30 text-xs font-semibold">Mentoring</span>
            <span className="px-3 py-1.5 rounded-full bg-gray-300 text-gray-600 border border-gray-400 text-xs font-semibold">Start</span>
          </div>

          <p className="dossier-label mt-4 mb-2">Après (palette dossier)</p>
          <div className="flex flex-wrap gap-2 p-4 bg-paper-2/40 rounded-lg border border-line">
            <span className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5" style={{ background: 'hsl(8 54% 39% / 0.08)', color: PALETTE.red, border: '1px solid hsl(8 54% 39% / 0.25)' }}><Shield className="w-3 h-3" /> Admin</span>
            <span className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5" style={{ background: 'hsl(41 67% 95%)', color: PALETTE.gold, border: '1px solid hsl(38 51% 84%)' }}><Award className="w-3 h-3" /> Major</span>
            <span className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5" style={{ background: 'hsl(167 27% 89%)', color: PALETTE.teal, border: '1px solid hsl(177 60% 21% / 0.25)' }}><Star className="w-3 h-3" /> Ambassadeur</span>
            <span className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5" style={{ background: 'hsl(218 48% 16% / 0.08)', color: PALETTE.ink, border: '1px solid hsl(218 48% 16% / 0.2)' }}><Compass className="w-3 h-3" /> Mentoring</span>
            <span className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5" style={{ background: PALETTE.paper2, color: PALETTE.muted, border: '1px solid hsl(41 30% 85%)' }}><Anchor className="w-3 h-3" /> Start</span>
          </div>
        </section>

        {/* Avis */}
        <section className="dossier-card mb-8" style={{ padding: '1.75rem' }}>
          <p className="dossier-label" style={{ margin: 0 }}>§ Analyse</p>
          <h2 className="text-2xl mt-1 mb-3 filet-gold">Avant / Après</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="font-semibold text-ink mb-2 font-serif">✅ Avantages du recoloring seul</p>
              <ul className="text-sm text-ink-soft space-y-1 pl-4 list-disc">
                <li>Zéro changement d'icône, zéro réapprentissage utilisateur</li>
                <li>Cohérence visuelle <strong>immédiate</strong></li>
                <li>5 couleurs sémantiques au lieu de 16 aléatoires</li>
                <li>Effort technique : minimal (1h)</li>
                <li>Pas de risque de régression UX</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-ink mb-2 font-serif">⚠️ Limites</p>
              <ul className="text-sm text-ink-soft space-y-1 pl-4 list-disc">
                <li>Reste très "SaaS générique" (Lucide line icons)</li>
                <li>Pas d'identité forte "carnet bancaire"</li>
                <li>Aucun élément distinctif (sceau, monogramme, médaille)</li>
                <li>Manque la dimension "luxe artisanal" du combo Pack 1+3</li>
              </ul>
            </div>
          </div>
          <div className="mt-6 p-4 rounded-lg" style={{ background: 'hsl(41 67% 95%)', borderLeft: '3px solid hsl(34 44% 50%)' }}>
            <p className="text-sm m-0 text-ink-soft">
              <strong className="text-ink">Verdict :</strong> le recoloring seul est <em>propre et rapide</em>, mais l'app continuera à ressembler à un SaaS classique. Pour vraiment évoquer un cabinet patrimonial premium, il faudra à terme ajouter au moins quelques éléments signature (sceau, médailles, monogramme).
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default IconsColorPreviewPage;
